import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<object|null>} The fetched cart
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      if (typeof window.__brightsideInvalidateCartCache === 'function') {
        window.__brightsideInvalidateCartCache();
      }

      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (typeof window.__brightsideRememberCart === 'function') {
        window.__brightsideRememberCart(cart);
      }

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Finds the nearest Skio plan picker for this product form.
   * @returns {Element | null}
   */
  #getSkioPlanPicker() {
    const section = this.closest('.shopify-section');
    return (
      section?.querySelector(
        'skio-plan-picker, .skio-plan-picker, [data-skio-plan-picker], [id*="skio-plan-picker"]'
      ) ||
      document.querySelector(
        'skio-plan-picker, .skio-plan-picker, [data-skio-plan-picker], [id*="skio-plan-picker"]'
      )
    );
  }

  /**
   * True when Skio's One-time purchase option is selected.
   * Radios live in Skio's shadow DOM; also treat an empty selling_plan input as OTP.
   * @param {Element | null | undefined} skioRoot
   * @returns {boolean}
   */
  #isSkioOneTimeSelected(skioRoot) {
    if (!skioRoot) return false;

    const roots = [skioRoot];
    if (skioRoot instanceof Element && skioRoot.shadowRoot) {
      roots.push(skioRoot.shadowRoot);
    }

    for (const root of roots) {
      const otpRadio =
        root.querySelector('input.group-input[value="One time purchase"]') ||
        root.querySelector('input.group-input[id^="one-time-"]') ||
        root.querySelector('input[type="radio"][value="One time purchase"]');

      if (otpRadio instanceof HTMLInputElement) {
        return otpRadio.checked;
      }
    }

    return false;
  }

  /**
   * True when a Skio Autoship/subscription group is selected.
   * @param {Element | null | undefined} skioRoot
   * @returns {boolean}
   */
  #isSkioAutoshipSelected(skioRoot) {
    if (!skioRoot) return false;

    const roots = [skioRoot];
    if (skioRoot instanceof Element && skioRoot.shadowRoot) {
      roots.push(skioRoot.shadowRoot);
    }

    for (const root of roots) {
      const autoshipRadio = root.querySelector(
        'input.group-input[id^="group-"]:checked, input.group-input[value]:not([value="One time purchase"]):checked'
      );
      if (autoshipRadio instanceof HTMLInputElement && autoshipRadio.checked) {
        return true;
      }
    }

    return false;
  }

  /**
   * Resolves the currently selected Shopify selling plan ID.
   * Skio Plan Picker (and similar) often render outside the product <form>, so
   * `new FormData(form)` alone can miss `selling_plan` even when the URL/UI show one.
   *
   * Priority (Skio-compatible):
   * 1) Skio one-time radio => no plan (never fall back to URL)
   * 2) Skio picker selling_plan field (empty = one-time; do not fall back to URL)
   * 3) Inputs associated with / inside this product form
   * 4) URL ?selling_plan= only when Autoship is clearly selected
   *
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  #getSelectedSellingPlanId(form) {
    /** @param {Element | null | undefined} el */
    const readValue = (el) => {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement) || el.disabled) {
        return '';
      }
      return String(el.value || '').trim();
    };

    /**
     * @param {ParentNode | null | undefined} root
     * @returns {{ found: boolean, value: string }}
     */
    const readFromRoot = (root) => {
      if (!root) return { found: false, value: '' };

      const fields = root.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]');
      let sawExplicitField = false;
      let lastNonEmpty = '';

      for (const el of fields) {
        if (el instanceof HTMLSelectElement) {
          if (el.disabled) continue;
          sawExplicitField = true;
          const value = readValue(el);
          if (value) return { found: true, value };
          continue;
        }

        if (!(el instanceof HTMLInputElement) || el.disabled) continue;

        if (el.type === 'radio') {
          if (!el.checked) continue;
          sawExplicitField = true;
          return { found: true, value: readValue(el) };
        }

        // Hidden/text: presence under Skio/form is an explicit field.
        // Empty value means one-time — do not fall through to URL.
        sawExplicitField = true;
        const value = readValue(el);
        if (value) lastNonEmpty = value;
      }

      if (sawExplicitField) {
        return { found: true, value: lastNonEmpty };
      }

      return { found: false, value: '' };
    };

    const skioRoot = this.#getSkioPlanPicker();

    // 1) Skio One-time is authoritative — never send a selling_plan.
    if (this.#isSkioOneTimeSelected(skioRoot)) {
      return '';
    }

    // 2) Skio picker selling_plan field (empty = one-time).
    const skioSelection = readFromRoot(skioRoot);
    if (skioSelection.found) return skioSelection.value;

    // 3) Native / form-associated selling_plan fields for this product form.
    const formId = form.id;
    if (formId) {
      const escapedId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(formId) : formId;
      const associated = document.querySelectorAll(`[name="selling_plan"][form="${escapedId}"]`);
      let sawAssociated = false;
      let associatedValue = '';

      for (const el of associated) {
        if (el instanceof HTMLSelectElement) {
          if (el.disabled) continue;
          sawAssociated = true;
          const value = readValue(el);
          if (value) return value;
          continue;
        }
        if (el instanceof HTMLInputElement) {
          if (el.disabled) continue;
          if (el.type === 'radio') {
            if (!el.checked) continue;
            return readValue(el);
          }
          sawAssociated = true;
          const value = readValue(el);
          if (value) associatedValue = value;
        }
      }

      if (sawAssociated) return associatedValue;
    }

    const formSelection = readFromRoot(form);
    if (formSelection.found) return formSelection.value;

    // 4) URL fallback only when Skio Autoship is positively selected.
    // Never re-apply a stale ?selling_plan= after switching to one-time.
    if (this.#isSkioAutoshipSelected(skioRoot)) {
      try {
        return new URLSearchParams(window.location.search).get('selling_plan')?.trim() || '';
      } catch {
        return '';
      }
    }

    return '';
  }

  /**
   * Ensures FormData includes the live Skio/theme selling_plan selection.
   * One-time purchase leaves selling_plan unset (Shopify 422 if a plan is sent).
   *
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   */
  #syncSellingPlanToFormData(form, formData) {
    const skioRoot = this.#getSkioPlanPicker();
    const isOneTime = this.#isSkioOneTimeSelected(skioRoot);
    const sellingPlanId = isOneTime ? '' : this.#getSelectedSellingPlanId(form);

    if (sellingPlanId) {
      formData.set('selling_plan', sellingPlanId);
      return;
    }

    // One-time / no plan: strip from FormData and clear any leftover DOM values
    // so associated inputs cannot reintroduce a stale plan.
    formData.delete('selling_plan');

    const clearTargets = [];
    if (skioRoot) clearTargets.push(skioRoot);
    clearTargets.push(form);

    if (form.id) {
      const escapedId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(form.id) : form.id;
      document.querySelectorAll(`[name="selling_plan"][form="${escapedId}"]`).forEach((el) => {
        clearTargets.push(el);
      });
    }

    clearTargets.forEach((root) => {
      if (!(root instanceof Element) && !(root instanceof DocumentFragment)) return;
      const fields =
        root instanceof HTMLInputElement || root instanceof HTMLSelectElement
          ? [root]
          : [...root.querySelectorAll('input[name="selling_plan"], select[name="selling_plan"]')];

      fields.forEach((el) => {
        if (el instanceof HTMLSelectElement && !el.disabled) {
          el.value = '';
        } else if (el instanceof HTMLInputElement && !el.disabled && el.type !== 'radio') {
          el.value = '';
        }
      });
    });
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    // Check if ANY add to cart button is disabled and do an early return if it is
    const anyButtonDisabled = Array.from(allAddToCartContainers).some(
      (container) => container.refs.addToCartButton?.disabled
    );
    if (anyButtonDisabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    // Skio selling_plan lives outside the <form>; include the live selection.
    this.#syncSellingPlanToFormData(form, formData);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    // Brightside's fetch interceptor strips selling_plan unless its own Auto-Ship
    // is selected. Skip that mutation when Skio (or any) selling_plan is present so
    // the selected plan reaches /cart/add.js unchanged.
    const hasSellingPlan = Boolean(formData.get('selling_plan'));
    /** @type {boolean | undefined} */
    const previousBrightsideSkip = window.__brightsideSkipSubscriptionCartPatch;
    if (hasSellingPlan) {
      window.__brightsideSkipSubscriptionCartPatch = true;
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
        ...(hasSellingPlan ? { 'X-Brightside-Cart-Upsell': '1' } : {}),
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          const cart = await this.#fetchAndUpdateCartQuantity();
// Klaviyo Added to Cart Tracking
window._learnq = window._learnq || [];

window._learnq.push([
  'track',
  'Added to Cart',
  {
    ProductID: formData.get('id'),
    ProductName: document.title,
    Quantity: Number(formData.get('quantity')) || 1
  }
]);
          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          if (cart?.items?.length) {
            document.dispatchEvent(
              new CustomEvent('brightside:cart-drawer-ready', {
                bubbles: true,
                detail: {
                  cart,
                  source: 'product-form-component',
                },
              }),
            );
          }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (hasSellingPlan) {
          window.__brightsideSkipSubscriptionCartPatch = previousBrightsideSkip;
        }
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';
    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
